import { useState } from 'react';
import { motion } from 'framer-motion';
import { CONTACT_INFO } from '@/lib/constants';
import SEO from '@/components/SEO';
import { useConversionTracking } from '@/hooks/useMarketingTracking';
import TrackedBookingLink from '@/components/TrackedBookingLink';
import { Phone, Mail, MapPin, Send, ShieldCheck } from 'lucide-react';

const FadeInWhenVisible = ({ children, delay = 0 }: { children: React.ReactNode, delay?: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.8, delay, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

export default function Contact() {
  const { trackContactForm, trackPhoneCall } = useConversionTracking();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    botField: '',
  });
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.botField) {
      setSubmitStatus('success');
      return;
    }
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      setSubmitStatus('error');
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email, 
          phone: formData.phone,
          message: formData.message,
        }),
      });
      if (!response.ok) throw new Error('Failed to submit');
      trackContactForm();
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '', botField: '' });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-brand-black min-h-screen">
      <SEO
        title="Contact Information & BJJ Classes Culver City | Delta BJJ"
        description="Drop us a line to secure your spot in a Brazilian Jiu-Jitsu class at Delta BJJ. We offer flexible Gi and No-Gi training in Culver City. Book your free trial!"
        url="https://www.deltabjj.com/contact-bjj-culver-city"
      />

      {/* Hero Header */}
      <section className="relative pt-48 pb-12 px-6 lg:px-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto text-center">
          <FadeInWhenVisible>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-display font-bold text-white tracking-tighter leading-tight mb-8 break-words px-4">
              GET IN <br />
              <span className="blue-gradient-text">TOUCH.</span>
            </h1>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Page Introduction Copy */}
      <section className="py-8 md:py-12 px-6 lg:px-12 border-b border-white/10">
        <div className="max-w-4xl mx-auto">
          <FadeInWhenVisible>
            <p className="text-base md:text-lg lg:text-xl text-white/80 leading-relaxed text-center">
              Ready to start your Jiu-Jitsu journey? Whether you're a total beginner or an experienced practitioner, we're here to help you get on the mats. Reach out today, and a member of our team will get back to you within 24 hours.
            </p>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Contact Section - Editorial Split */}
      <section className="py-16 md:py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-16">
          {/* Info Column */}
          <div className="lg:col-span-5">
            <FadeInWhenVisible>
              <h2 className="text-3xl md:text-5xl font-display font-bold text-brand-black mb-8 tracking-tighter leading-none">
                OUR MODERN <br />
                <span className="text-gray-200">FACILITY.</span>
              </h2>
              <div className="space-y-8">
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(CONTACT_INFO.address)}`} target="_blank" rel="noopener noreferrer" className="group flex items-start gap-6">
                  <div className="w-12 h-12 rounded-sm bg-gray-50 flex items-center justify-center group-hover:bg-brand-blue transition-all shrink-0">
                    <MapPin className="w-5 h-5 text-brand-blue group-hover:text-brand-black transition-colors" />
                  </div>
                  <div>
                    <span className="block text-[10px] text-gray-400 uppercase tracking-widest mb-2 font-bold">Location</span>
                    <span className="text-lg font-light text-brand-black leading-relaxed">{CONTACT_INFO.address}</span>
                  </div>
                </a>
                
                <a href={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`} onClick={trackPhoneCall} className="group flex items-start gap-6">
                  <div className="w-12 h-12 rounded-sm bg-gray-50 flex items-center justify-center group-hover:bg-brand-blue transition-all shrink-0">
                    <Phone className="w-5 h-5 text-brand-blue group-hover:text-brand-black transition-colors" />
                  </div>
                  <div>
                    <span className="block text-[10px] text-gray-400 uppercase tracking-widest mb-2 font-bold">Direct Line</span>
                    <span className="text-lg font-light text-brand-black leading-relaxed">{CONTACT_INFO.phone}</span>
                  </div>
                </a>

                <a href={`mailto:${CONTACT_INFO.email}`} className="group flex items-start gap-6">
                  <div className="w-12 h-12 rounded-sm bg-gray-50 flex items-center justify-center group-hover:bg-brand-blue transition-all shrink-0">
                    <Mail className="w-5 h-5 text-brand-blue group-hover:text-brand-black transition-colors" />
                  </div>
                  <div>
                    <span className="block text-[10px] text-gray-400 uppercase tracking-widest mb-2 font-bold">Inquiries</span>
                    <span className="text-lg font-light text-brand-black leading-relaxed">{CONTACT_INFO.email}</span>
                  </div>
                </a>
              </div>
              
              {/* Map Placeholder */}
              <div className="mt-12 relative aspect-video overflow-hidden rounded-sm border border-gray-100 shadow-2xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3306.5!2d-118.395!3d34.0123!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2b9b8c8c8c8c9%3A0x0!2zMTEwMjQgV2FzaGluZ3RvbiBCbHZkLCBDdWx2ZXIgQ2l0eSwgQ0EgOTAyMzI!5e0!3m2!1sen!2sus!4v1234567890!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'grayscale(1) contrast(1.2)' }}
                  allowFullScreen
                  loading="lazy"
                  title="Delta BJJ Location"
                ></iframe>
              </div>
            </FadeInWhenVisible>
          </div>

          {/* Form Column */}
          <div className="lg:col-span-7 bg-gray-50 p-8 md:p-12 rounded-sm">
            <FadeInWhenVisible delay={0.2}>
              <h2 className="text-2xl md:text-3xl font-display font-bold text-brand-black mb-4 tracking-tighter">What Happens Next?</h2>
              <p className="text-base md:text-lg text-gray-700 mb-6 leading-relaxed">
                Drop us a line to secure your spot in one of our Brazilian Jiu-Jitsu classes.
              </p>
              <div className="space-y-3 mb-8">
                <div className="flex items-start gap-4">
                  <span className="text-brand-blue font-display font-bold text-lg flex-shrink-0">Step 1:</span>
                  <p className="text-gray-700 text-sm md:text-base">Fill out the form.</p>
                </div>
                <div className="flex items-start gap-4">
                  <span className="text-brand-blue font-display font-bold text-lg flex-shrink-0">Step 2:</span>
                  <p className="text-gray-700 text-sm md:text-base">We'll reach out to schedule your first visit.</p>
                </div>
                <div className="flex items-start gap-4">
                  <span className="text-brand-blue font-display font-bold text-lg flex-shrink-0">Step 3:</span>
                  <p className="text-gray-700 text-sm md:text-base">Show up, meet the team, and start training!</p>
                </div>
              </div>
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Your Name</label>
                    <input
                      type="text" name="name" required value={formData.name} onChange={handleChange}
                      className="w-full bg-transparent border-b border-gray-200 py-4 focus:border-brand-blue outline-none transition-colors text-brand-black font-light"
                      placeholder="John Smith"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Email Address</label>
                    <input
                      type="email" name="email" required value={formData.email} onChange={handleChange}
                      className="w-full bg-transparent border-b border-gray-200 py-4 focus:border-brand-blue outline-none transition-colors text-brand-black font-light"
                      placeholder="student@example.com"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Phone</label>
                <input
                type="tel" name="phone" value={formData.phone} onChange={handleChange} required
                className="w-full bg-transparent border-b border-gray-200 py-4 focus:border-brand-blue outline-none transition-colors text-brand-black font-light"
                placeholder="+1 (310) 000-0000"
                />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Your Message</label>
                  <textarea
                    name="message" required rows={4} value={formData.message} onChange={handleChange}
                    className="w-full bg-transparent border-b border-gray-200 py-4 focus:border-brand-blue outline-none transition-colors text-brand-black font-light resize-none"
                    placeholder="Tell us about your goals..."
                  />
                </div>
                
                {submitStatus === 'success' && (
                  <div className="bg-brand-black text-brand-blue p-6 rounded-sm text-xs font-bold tracking-widest uppercase flex items-center gap-4">
                    <ShieldCheck className="w-5 h-5" />
                    <span>Inquiry sent. A master will contact you shortly.</span>
                  </div>
                )}
                
                <button
                  type="submit" disabled={isSubmitting}
                  className="btn-premium-blue w-full flex items-center justify-center gap-4 text-base md:text-lg font-extrabold py-4"
                >
                  <span>{isSubmitting ? 'Processing...' : 'Send Inquiry'}</span>
                  <Send className="w-5 h-5" />
                </button>
              </form>
            </FadeInWhenVisible>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 px-6 lg:px-12 text-center bg-gradient-to-br from-brand-blue/10 via-white to-brand-blue/5 relative overflow-hidden border-t-4 border-brand-blue/20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(29,76,162,0.1),transparent_50%)] pointer-events-none"></div>
        <FadeInWhenVisible>
          <div className="max-w-4xl mx-auto relative z-10">
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-display font-bold text-brand-black mb-6 leading-none tracking-tighter">
              START YOUR <br />
              <span className="text-brand-blue">LEGACY.</span>
            </h2>
            <TrackedBookingLink href="/#book-trial" className="btn-premium-blue text-base px-8 py-4 font-extrabold">
              Book 2 Free Trials
            </TrackedBookingLink>
          </div>
        </FadeInWhenVisible>
      </section>

      {/* Footer Separator - Clear visual break */}
      <div className="h-1 bg-gradient-to-r from-transparent via-brand-blue/30 to-transparent"></div>
    </div>
  );
}
